import { Component, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { animate, style, transition, trigger } from '@angular/animations';
import { combineLatest, filter, finalize, map, Observable, Subject, takeUntil, tap } from 'rxjs';
import { GetSummaryWithWeeklyTrendRequestParams, TransactionDetailSummaryResult } from './interfaces/dashboards.interface';
import { GetProviderRequestParams } from './pages/providers/providers.interface';
import { GetProductRequestParams } from './pages/products/products.interface';
import { GetMerchantRequestParams } from './pages/merchants/merchants.interface';

import { DashboardsService } from './services/dashboards/dashboards.service';
import { ProvidersService } from './services/providers/providers.service';
import { MerchantsService } from './services/merchants/merchants.service';
import { ProductsService } from './services/products/products.service';
import { UserData } from './interfaces/users.interface';
import { selectCurrentUser } from './services/users/user.selectors';
import { DepositsService } from './services/deposits/deposits.service';
import { GetDepositRequestParams, GetDepositResponse } from './pages/deposits/deposits.interface';

@Component({
  moduleId: module.id,
  templateUrl: './index.html',
  animations: [
    trigger('toggleAnimation', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'scale(0.95)',
        }),
        animate('100ms ease-out', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
      transition(':leave', [animate('75ms', style({ opacity: 0, transform: 'scale(0.95)' }))]),
    ]),
  ],
})
export class IndexComponent implements OnDestroy {
  store: any;
  revenueChart: any;
  salesByCategory: any;
  dailySales: any;
  totalOrders: any;

  balance: any;
  dataTransactionDetailSummary!: TransactionDetailSummaryResult;
  totalProducts: any;
  totalMerchants: any;
  totalProvider: any;

  userData$!: Observable<UserData | null>;
  isAdmin$!: Observable<boolean>;
  isMerchant$!: Observable<boolean>;

  isLoading = true;
  loading = false;
  private destroy$ = new Subject<void>();

  depositResponseParam: GetDepositResponse = {
    code: 0,
    message: '',
    data: [],
    currPage: 0,
    haveNext: false,
    totalPage: 0,
  };
  params = new GetSummaryWithWeeklyTrendRequestParams();

  constructor(
    public storeData: Store<any>,
    private dashboardService: DashboardsService,
    private providersService: ProvidersService,
    private productsService: ProductsService,
    private depositService: DepositsService,
    private merchantsService: MerchantsService
  ) {
    // Panggil init yang tidak bergantung pada peran

    // Panggil init yang bergantung pada peran pengguna
    this.initRoleBasedDataFetching();

    this.initSummaryWithWeeklyTrend();
    this.initGetProducts();
    this.initGetDeposits();

    // Panggil init yang bergantung pada perubahan UI (tema, layout)
    this.initUIBasedUpdates();

    this.isLoading = false;
  }

  /**
   * Mengatur langganan untuk pembaruan yang dipicu oleh perubahan UI, seperti tema.
   */
  initUIBasedUpdates(): void {
    this.storeData
      .select((d) => d.index)
      .pipe(takeUntil(this.destroy$))
      .subscribe((d) => {
        const hasChangeTheme = this.store?.theme !== d?.theme;
        const hasChangeLayout = this.store?.layout !== d?.layout;
        const hasChangeMenu = this.store?.menu !== d?.menu;
        const hasChangeSidebar = this.store?.sidebar !== d?.sidebar;

        this.store = d;

        if (hasChangeTheme || hasChangeLayout || hasChangeMenu || hasChangeSidebar) {
          // Hanya muat ulang chart saat UI berubah
          this.initCharts();
          this.initSummaryWithWeeklyTrend();
        }
      });
  }

  /**
   * Mengatur logika utama untuk mengambil data berdasarkan peran pengguna.
   * Ini adalah satu-satunya tempat di mana keputusan dibuat.
   */
  initRoleBasedDataFetching(): void {
    // 1. Ambil data user dari store dan filter emisi awal yang null
    this.userData$ = this.storeData.select(selectCurrentUser).pipe(
      filter((user) => !!user), // Aliran 2: hanya user yang tidak null yang lolos

      tap((user) => {
        this.balance = user?.merchant?.balance;
      })
    );

    // 2. Buat observable untuk setiap peran dari userData$ yang sudah difilter
    this.isAdmin$ = this.userData$.pipe(map((user) => user?.roles?.some((role) => role.code === 'admin') ?? false));
    this.isMerchant$ = this.userData$.pipe(map((user) => user?.roles?.some((role) => role.code === 'merchant') ?? false));

    // 3. Gabungkan observable peran untuk membuat satu keputusan yang valid
    combineLatest([this.isAdmin$, this.isMerchant$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([isAdmin, isMerchant]) => {
        console.log(`Pengecekan Peran: Admin=${isAdmin}, Merchant=${isMerchant}`);

        // 4. Buat keputusan: Panggil fungsi jika user adalah admin ATAU BUKAN merchant
        if (isAdmin || !isMerchant) {
          console.log('Akses diizinkan: Memuat data Merchants dan Providers.');
          this.initGetMerchants();
          this.initGetProviders();
        } else {
          console.log('Akses Merchant-only: Melewatkan data Merchants dan Providers.');
          // Reset data agar tidak menampilkan nilai lama
          this.totalMerchants = 0;
          this.totalProvider = 0;
        }
      });
  }

  /**
   * Mengambil data Provider. Fungsi ini tidak lagi peduli tentang peran.
   */
  initGetProviders(): void {
    this.loading = true;
    this.providersService
      .getProviderService(new GetProviderRequestParams())
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response) => {
          this.totalProvider = response.totalPage;
        },
        error: (err) => console.error('Error fetching provider data:', err),
      });
  }

  /**
   * Mengambil data Merchant. Fungsi ini tidak lagi peduli tentang peran.
   */
  initGetMerchants(): void {
    this.loading = true;
    this.merchantsService
      .getMerchantService(new GetMerchantRequestParams())
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response) => {
          this.totalMerchants = response.totalPage;
        },
        error: (err) => console.error('Error fetching merchants data:', err),
      });
  }

  initSummaryWithWeeklyTrend(): void {
    this.loading = true;

    this.dashboardService
      .getSummaryWithWeeklyTrend(this.params)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response) => {
          this.dataTransactionDetailSummary = response.data;
          const thisWeek = response.data.success_transaction_detail_this_and_last_week.this_week;
          const lastWeek = response.data.success_transaction_detail_this_and_last_week.last_week;
          if (this.dailySales) {
            this.dailySales.series = [
              { name: 'This Week', data: thisWeek.map((d: any) => d.total_success) },
              { name: 'Last Week', data: lastWeek.map((d: any) => d.total_success) },
            ];
            this.dailySales.xaxis.categories = thisWeek.map((d: any) => d.day_name);
          }
        },
        error: (err) => console.error('Error fetching GetSummaryWithWeeklyTrend data:', err),
      });
  }

  initGetProducts(): void {
    this.loading = true;
    this.productsService
      .getProductService(new GetProductRequestParams())
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response) => {
          this.totalProducts = response.totalPage;
        },
        error: (err) => console.error('Error fetching products data:', err),
      });
  }

  initGetDeposits(): void {
    this.loading = true;
    this.depositService
      .getDepositService(new GetDepositRequestParams())
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response) => {
          this.depositResponseParam = response;
        },
        error: (err) => console.error('Error fetching products data:', err),
      });
  }

  getSummaryPercentage(value: number, max: number): string {
    if (max === 0) return '0%';
    const percentage = Math.round((value / max) * 100);
    return `${percentage}%`;
  }

  getSummaryMaxEntityCount(): number {
    return Math.max(this.totalMerchants || 0, this.totalProvider || 0, this.totalProducts || 0);
  }

  /**
   * Returns the sum of SUCCESS-status amount for the given period.
   * Used by the dashboard Row 2 (total amount) cards.
   */
  getSuccessAmount(period: 'today' | 'week' | 'month' | 'year'): number {
    const data = this.dataTransactionDetailSummary;
    if (!data || !data.summary_transaction_detail_by_status) return 0;
    const successItem = data.summary_transaction_detail_by_status.find((x) => x.status === '0');
    if (!successItem) return 0;
    const key = ('amount_' + period) as 'amount_today' | 'amount_week' | 'amount_month' | 'amount_year';
    return successItem[key] || 0;
  }

  /**
   * Computes success rate = success / (success + pending + failed).
   *
   * Excluded from the denominator (not actual payment outcomes):
   *   - INQUIRY ('3') — the customer was only checking the bill, never committed
   *   - REVERSAL ('4') — an undo of a prior transaction, not a fresh attempt
   *
   * Returns a formatted percentage like "78.5%", or "—" when there's no data.
   * Used by the dashboard Row 3 (success rate) cards.
   */
  getSuccessRate(period: 'today' | 'week' | 'month' | 'year'): string {
    const data = this.dataTransactionDetailSummary;
    if (!data || !data.summary_transaction_detail_by_status) return '—';
    const key = ('total_' + period) as 'total_today' | 'total_week' | 'total_month' | 'total_year';
    let successCount = 0;
    let totalCount = 0;
    for (const item of data.summary_transaction_detail_by_status) {
      // Only count actual payment outcomes: SUCCESS / PENDING / FAILED.
      if (item.status !== '0' && item.status !== '1' && item.status !== '2') continue;
      const c = item[key] || 0;
      totalCount += c;
      if (item.status === '0') successCount = c;
    }
    if (totalCount === 0) return '—';
    const rate = (successCount / totalCount) * 100;
    return rate.toFixed(1) + '%';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initCharts() {
    const isDark = this.store.theme === 'dark' || this.store.isDarkMode ? true : false;
    const isRtl = this.store.rtlClass === 'rtl' ? true : false;

    // revenue
    this.revenueChart = {
      chart: {
        height: 325,
        type: 'area',
        fontFamily: 'Nunito, sans-serif',
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        curve: 'smooth',
        width: 2,
        lineCap: 'square',
      },
      dropShadow: {
        enabled: true,
        opacity: 0.2,
        blur: 10,
        left: -7,
        top: 22,
      },
      colors: isDark ? ['#2196f3', '#e7515a'] : ['#1b55e2', '#e7515a'],
      markers: {
        discrete: [
          {
            seriesIndex: 0,
            dataPointIndex: 6,
            fillColor: '#1b55e2',
            strokeColor: 'transparent',
            size: 7,
          },
          {
            seriesIndex: 1,
            dataPointIndex: 5,
            fillColor: '#e7515a',
            strokeColor: 'transparent',
            size: 7,
          },
        ],
      },
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      xaxis: {
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        crosshairs: {
          show: true,
        },
        labels: {
          offsetX: isRtl ? 2 : 0,
          offsetY: 5,
          style: {
            fontSize: '12px',
            cssClass: 'apexcharts-xaxis-title',
          },
        },
      },
      yaxis: {
        tickAmount: 7,
        labels: {
          formatter: (value: number) => {
            return value / 1000 + 'K';
          },
          offsetX: isRtl ? -30 : -10,
          offsetY: 0,
          style: {
            fontSize: '12px',
            cssClass: 'apexcharts-yaxis-title',
          },
        },
        opposite: isRtl ? true : false,
      },
      grid: {
        borderColor: isDark ? '#191e3a' : '#e0e6ed',
        strokeDashArray: 5,
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '16px',
        markers: {
          width: 10,
          height: 10,
          offsetX: -2,
        },
        itemMargin: {
          horizontal: 10,
          vertical: 5,
        },
      },
      tooltip: {
        marker: {
          show: true,
        },
        x: {
          show: false,
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          inverseColors: !1,
          opacityFrom: isDark ? 0.19 : 0.28,
          opacityTo: 0.05,
          stops: isDark ? [100, 100] : [45, 100],
        },
      },
      series: [
        {
          name: 'Income',
          data: [16800, 16800, 15500, 17800, 15500, 17000, 19000, 16000, 15000, 17000, 14000, 17000],
        },
        {
          name: 'Expenses',
          data: [16500, 17500, 16200, 17300, 16000, 19500, 16000, 17000, 16000, 19000, 18000, 19000],
        },
      ],
    };

    // sales by category
    this.salesByCategory = {
      chart: {
        type: 'donut',
        height: 460,
        fontFamily: 'Nunito, sans-serif',
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 25,
        colors: isDark ? '#0e1726' : '#fff',
      },
      colors: isDark ? ['#5c1ac3', '#e2a03f', '#e7515a', '#e2a03f'] : ['#e2a03f', '#5c1ac3', '#e7515a'],
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '14px',
        markers: {
          width: 10,
          height: 10,
          offsetX: -2,
        },
        height: 50,
        offsetY: 20,
      },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            background: 'transparent',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '29px',
                offsetY: -10,
              },
              value: {
                show: true,
                fontSize: '26px',
                color: isDark ? '#bfc9d4' : undefined,
                offsetY: 16,
                formatter: (val: any) => {
                  return val;
                },
              },
              total: {
                show: true,
                label: 'Total',
                color: '#888ea8',
                fontSize: '29px',
                formatter: (w: any) => {
                  return w.globals.seriesTotals.reduce(function (a: any, b: any) {
                    return a + b;
                  }, 0);
                },
              },
            },
          },
        },
      },
      labels: ['Apparel', 'Sports', 'Others'],
      states: {
        hover: {
          filter: {
            type: 'none',
            value: 0.15,
          },
        },
        active: {
          filter: {
            type: 'none',
            value: 0.15,
          },
        },
      },
      series: [985, 737, 270],
    };

    // daily sales
    this.dailySales = {
      chart: {
        height: 160,
        type: 'bar',
        fontFamily: 'Nunito, sans-serif',
        toolbar: {
          show: false,
        },
        stacked: true,
        stackType: '100%',
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 1,
      },
      colors: ['#e2a03f', '#e0e6ed'],
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: 'bottom',
              offsetX: -10,
              offsetY: 0,
            },
          },
        },
      ],
      xaxis: {
        labels: {
          show: false,
        },
        categories: ['Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun'],
      },
      yaxis: {
        show: false,
      },
      fill: {
        opacity: 1,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '25%',
        },
      },
      legend: {
        show: false,
      },
      grid: {
        show: false,
        xaxis: {
          lines: {
            show: false,
          },
        },
        padding: {
          top: 10,
          right: -20,
          bottom: -20,
          left: -20,
        },
      },
      series: [
        {
          name: 'Sales',
          data: [44, 55, 41, 67, 22, 43, 21],
        },
        {
          name: 'Last Week',
          data: [13, 23, 20, 8, 13, 27, 33],
        },
      ],
    };

    // total orders
    this.totalOrders = {
      chart: {
        height: 290,
        type: 'area',
        fontFamily: 'Nunito, sans-serif',
        sparkline: {
          enabled: true,
        },
      },
      stroke: {
        curve: 'smooth',
        width: 2,
      },
      colors: isDark ? ['#00ab55'] : ['#00ab55'],
      labels: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
      yaxis: {
        min: 0,
        show: false,
      },
      grid: {
        padding: {
          top: 125,
          right: 0,
          bottom: 0,
          left: 0,
        },
      },
      fill: {
        opacity: 1,
        type: 'gradient',
        gradient: {
          type: 'vertical',
          shadeIntensity: 1,
          inverseColors: !1,
          opacityFrom: 0.3,
          opacityTo: 0.05,
          stops: [100, 100],
        },
      },
      tooltip: {
        x: {
          show: false,
        },
      },
      series: [
        {
          name: 'Sales',
          data: [28, 40, 36, 52, 38, 60, 38, 52, 36, 40],
        },
      ],
    };
  }
}
